import { Resource, Booking, Project, BudgetStatus, ProjectBudget, ResourceAllocation } from '@/types';
import { differenceInHours, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';

export function calculateBudgetStatus(consumed: number, budget: number): BudgetStatus {
  const percentage = (consumed / budget) * 100;
  
  if (percentage > 100) return 'red';
  if (percentage >= 80) return 'amber';
  return 'green';
}

export function calculateProjectBudgets(projects: Project[]): ProjectBudget[] {
  return projects.map((project) => {
    const remainingDays = project.budgetDays - project.consumedDays;
    const status = calculateBudgetStatus(project.consumedDays, project.budgetDays);
    
    return {
      projectId: project.id,
      projectName: project.name,
      budgetDays: project.budgetDays,
      consumedDays: project.consumedDays,
      remainingDays,
      status,
    };
  });
}

export function calculateResourceUtilization(
  resources: Resource[],
  bookings: Booking[],
  startDate: Date,
  endDate: Date
): ResourceAllocation[] {
  const workingHoursPerDay = 8;
  const daysInPeriod = eachDayOfInterval({ start: startDate, end: endDate }).length;
  const totalCapacityHours = daysInPeriod * workingHoursPerDay;

  return resources.map((resource) => {
    const resourceBookings = bookings.filter((b) => String(b.resourceId) === String(resource.id));
    
    const allocatedHours = resourceBookings.reduce((total, booking) => {
      const hours = differenceInHours(booking.endTime, booking.startTime);
      return total + hours;
    }, 0);

    const utilizationPercent = (allocatedHours / totalCapacityHours) * 100;

    return {
      resourceId: resource.id,
      resourceName: resource.name,
      allocatedHours,
      capacity: totalCapacityHours,
      utilizationPercent: Math.round(utilizationPercent),
    };
  });
}

export function getUtilizationColor(utilization: number): string {
  if (utilization < 50) return 'bg-blue-500'; // Underutilized
  if (utilization >= 50 && utilization <= 85) return 'bg-green-500'; // Optimal
  if (utilization > 85 && utilization <= 100) return 'bg-amber-500'; // Near capacity
  return 'bg-red-500'; // Overloaded
}

/**
 * @deprecated Use getUtilizationLabel from '@/utils/utilization-labels' with settings tiers instead
 * This function is kept for backward compatibility but should be replaced
 */
export function getUtilizationLabel(utilization: number): string {
  if (utilization === 0) return 'No bookings';
  if (utilization < 50) return 'Underutilized';
  if (utilization >= 50 && utilization <= 85) return 'Optimal';
  if (utilization > 85 && utilization <= 100) return 'Near capacity';
  return 'Overloaded';
}
